import cookie from "js-cookie";
import { debounce, isNil, mapKeys, merge, noop } from "lodash";

import { desktopVersion as getDesktopVersion } from "~/lib/version";

import { inCalendar, inVideocall } from "./browserExtension";
import store from "./store";
import { getPlatform, isNative } from "./utils/platform";

const UTM_STORE_KEY = "fellow_referrer";
const UTM_LAST_TOUCH_STORE_KEY = "fellow_referrer_last_touch";
const UTM_TIMESTAMP_STORE_KEY = "fellow_referrer_timestamp";

function getCommonOptions() {
	return {
		integrations: {
			INTERCOM: !window.hideIntercom(),
			Intercom: !window.hideIntercom(),
			STITCH_DATA: false,
			"Stitch Data": false,
		},
	};
}

function getCommonProperties() {
	/* eslint-disable camelcase */
	const fetchedDesktopVersion = getDesktopVersion();
	return {
		is_desktop_app: !!fetchedDesktopVersion,
		...(fetchedDesktopVersion ? { desktop_app_version: `FellowElectron:${fetchedDesktopVersion.join(".")}` } : {}),
		capacitor_is_native: isNative,
		capacitor_platform: getPlatform(),
		is_embedded: inVideocall || inCalendar,
		client_session_id: window.CLIENT_SESSION_ID,
	};
	/* eslint-enable camelcase */
}

export function dispatchAnalyticsPage(category?: string, name?: string, properties: object = {}, options: object = {}) {
	const callTypeDefaultOptions = {
		integrations: {
			FACEBOOK_PIXEL: true,
			"Facebook Pixel": true,
			LINKEDIN_INSIGHT_TAG: true,
			"Linkedin Insight Tag": true,
		},
	};

	const actualProperties = merge(getCommonProperties(), properties);
	const actualOptions = merge(getCommonOptions(), callTypeDefaultOptions, options);

	window.rudderanalytics.page(category, name, actualProperties, actualOptions);
}

export function dispatchAnalyticsTrack(event: string, properties: object = {}, options: object = {}) {
	if (process.env.NODE_ENV !== "production") {
		console.debug("[dispatchAnalyticsTrack]", event, properties, options);
	}
	const callTypeDefaultOptions = {
		integrations: {
			FACEBOOK_PIXEL: false,
			INTERCOM: false,
			"Facebook Pixel": false,
			Intercom: false,
			LINKEDIN_INSIGHT_TAG: false,
			"Linkedin Insight Tag": false,
		},
	};

	const actualProperties = merge(getCommonProperties(), properties);
	const actualOptions = merge(getCommonOptions(), callTypeDefaultOptions, options);

	window.rudderanalytics.track(event, actualProperties, actualOptions);
}

/**
 * Tracks conversion events that should be sent to ad platforms (Facebook, LinkedIn).
 * These events are routed through RudderStack to the pixel destinations configured on the
 * "Fellow Production Core --- Website" source.
 *
 * All integrations are disabled by default to avoid clashing with server-side events,
 * then only ad platforms are explicitly enabled.
 *
 * Standard conversion event names that map to platform-specific events:
 * - "Initiate Checkout" → Facebook: InitiateCheckout, LinkedIn: start_checkout
 * - "Workspace Created" → Custom event for ad attribution
 * - "Business Workspace Created" → Custom event for business workspace conversions
 * - "Manager Business Workspace Created" → Custom event for manager-led workspace conversions
 */
export function dispatchConversionEvent(event: string, properties: object = {}) {
	if (process.env.NODE_ENV !== "production") {
		console.debug("[dispatchConversionEvent]", event, properties);
	}

	return dispatchAnalyticsTrack(event, properties, {
		integrations: {
			All: false, // Disable all integrations by default
			// Explicitly enable only ad platforms
			FACEBOOK_PIXEL: true,
			"Facebook Pixel": true,
			LINKEDIN_INSIGHT_TAG: true,
			"Linkedin Insight Tag": true,
		},
	});
}

export function resetAnalyticsUser() {
	window.heap?.resetIdentity();
	window.rudderanalytics.reset();
}

/**
 * Company size tier mapping based on employee count.
 * Maps to warehouse company__size_category values.
 */
type CompanySizeTier = "Enterprise" | "Mid-Market" | "SMB" | "Small" | "Unknown";

export function getCompanySizeTier(employeeCount: number | null | undefined): CompanySizeTier {
	if (isNil(employeeCount)) {
		return "Unknown";
	}
	if (employeeCount >= 1000) {
		return "Enterprise";
	}
	if (employeeCount >= 200) {
		return "Mid-Market";
	}
	if (employeeCount >= 50) {
		return "SMB";
	}
	if (employeeCount >= 10) {
		return "Small";
	}
	return "Unknown";
}

/**
 * Calculate expected lead value based on historical conversion rates and average revenue.
 * Formula: Expected Value = Conversion Rate × Average Annual Revenue
 *
 * Values derived from analysis of 2024+ workspace cohorts:
 * - Managers have ~20% conversion rate vs ~6% for non-managers
 * - Company size significantly impacts deal value
 *
 * @param employeeCount - Enriched employee count from company data
 * @param isManager - Whether the workspace creator identified as a manager
 * @returns The predicted value and the tier used to compute it
 */
export function getExpectedLeadValue(
	employeeCount: number | null | undefined,
	isManager: boolean,
): { value: number; companySizeTier: CompanySizeTier } {
	const companySizeTier = getCompanySizeTier(employeeCount);

	// Expected values based on historical data:
	// Expected Value = P(conversion) × E[annual revenue]
	const valueMatrix: Record<CompanySizeTier, { manager: number; nonManager: number }> = {
		Enterprise: { manager: 514, nonManager: 158 },
		"Mid-Market": { manager: 582, nonManager: 68 },
		SMB: { manager: 667, nonManager: 108 },
		Small: { manager: 277, nonManager: 52 },
		Unknown: { manager: 226, nonManager: 32 },
	};

	const tierValues = valueMatrix[companySizeTier];
	return { value: isManager ? tierValues.manager : tierValues.nonManager, companySizeTier };
}

export const UTM_PARAMS_OF_INTEREST = [
	"utm_campaign",
	"utm_medium",
	"utm_source",
	"utm_content",
	"utm_term",
	"gclid",
	"fbclid",
	"msclkid",
	"clickid",
	"li_fat_id",
];

const COOKIES_OF_INTEREST = [
	"_ga",
	"_gat",
	"_gid",
	"_gcl_au",
	"_gac",
	"_fbp",
	"_fbc",
	"_uetsid",
	"_uetvid",
	"__hstc",
	"__hssc",
	"__hssrc",
];

export function setReferringEntities() {
	const existing = store.has(UTM_STORE_KEY);

	if (!existing) {
		const result: Record<string, string> = {};
		const params = new URLSearchParams(window.location.search);
		for (const [name, value] of params.entries()) {
			if (UTM_PARAMS_OF_INTEREST.includes(name.toLowerCase())) {
				result[name.toLowerCase()] = value;
			}
		}
		store.set(UTM_STORE_KEY, result);
	}

	// Set _fbc cookie from fbclid URL parameter if present
	// This handles the case where the cookie bridge fails to transfer _fbc
	// from the marketing domain due to browser privacy restrictions
	setFbcFromUrlParam();
}

/**
 * Sets Facebook tracking cookies from URL parameters.
 *
 * This handles the case where the cookie bridge fails to transfer _fbc/_fbp
 * from the marketing domain (fellow.ai) due to browser privacy restrictions
 * (Safari ITP, Chrome third-party cookie blocking, etc.).
 *
 * Supports:
 * - fbclid: Converted to _fbc format (fb.{version}.{timestamp}.{fbclid})
 * - _fbc: Used directly if already in cookie format
 * - _fbp: Used directly if passed as URL param
 */
export function setFbcFromUrlParam() {
	const params = new URLSearchParams(window.location.search);

	// Handle _fbc - either from direct param or constructed from fbclid
	const fbcParam = params.get("_fbc");
	const fbclid = params.get("fbclid");
	const existingFbc = cookie.get("_fbc");

	if (!existingFbc) {
		if (fbcParam) {
			// Use _fbc directly if passed as URL param
			cookie.set("_fbc", fbcParam, {
				expires: 90,
				path: "/",
				secure: true,
				sameSite: "lax",
			});
		} else if (fbclid) {
			// Construct _fbc from fbclid in Facebook's expected format
			const version = 1;
			const creationTime = Date.now();
			const fbcValue = `fb.${version}.${creationTime}.${fbclid}`;

			cookie.set("_fbc", fbcValue, {
				expires: 90,
				path: "/",
				secure: true,
				sameSite: "lax",
			});
		}
	}

	// Handle _fbp if passed as URL param
	const fbpParam = params.get("_fbp");
	const existingFbp = cookie.get("_fbp");

	if (fbpParam && !existingFbp) {
		cookie.set("_fbp", fbpParam, {
			expires: 90,
			path: "/",
			secure: true,
			sameSite: "lax",
		});
	}
}

export function getCookiesOfInterest() {
	const result: Record<string, string> = {};
	for (const name of COOKIES_OF_INTEREST) {
		const value = cookie.get(name);
		if (value) {
			result[name] = value;
		}
	}
	return result;
}

export function getReferringEntities(): Record<string, string> {
	const referrer = store.get(UTM_STORE_KEY, {});
	const ltReferrer = store.get(UTM_LAST_TOUCH_STORE_KEY, {});
	const referrerTimestamp = store.get(UTM_TIMESTAMP_STORE_KEY, null);

	const timestampData = referrerTimestamp ? { timestamp: referrerTimestamp } : {};

	// Include Facebook tracking cookies so they survive OAuth redirect
	// These cookies may be lost during the redirect to Google/Microsoft OAuth
	const fbCookies: Record<string, string> = {};
	const fbc = cookie.get("_fbc");
	const fbp = cookie.get("_fbp");
	if (fbc) fbCookies["_fbc"] = fbc;
	if (fbp) fbCookies["_fbp"] = fbp;

	return merge(
		referrer,
		mapKeys(ltReferrer, (value, key) => `lt_${key}`),
		timestampData,
		fbCookies,
	);
}

export function getFellowAnonymousIdList(): string[] {
	try {
		const value = localStorage.getItem("fellow_anonymous_id_list");
		if (!value) return [];

		// Parse as JSON array (cookie bridge stores as JSON)
		const parsed = JSON.parse(value);
		return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
	} catch (error) {
		// localStorage might not be available (private browsing, disabled, etc.)
		console.debug("localStorage unavailable for anonymous ID list:", error);
		return [];
	}
}

const debouncedInteractionTracker = debounce(
	() => {
		let data = new FormData();

		data.append("outer_height", window.outerHeight.toString());
		data.append("outer_width", window.outerWidth.toString());
		data.append("inner_height", window.innerHeight.toString());
		data.append("inner_width", window.innerWidth.toString());

		data.append("screen_height", window.screen.height.toString());
		data.append("screen_width", window.screen.width.toString());
		data.append("screen_avail_height", window.screen.availHeight.toString());
		data.append("screen_avail_width", window.screen.availWidth.toString());

		data.append("device_pixel_ratio", window.devicePixelRatio.toString());
		data.append("has_vertical_scrollbar", (document.body.scrollHeight > window.innerHeight).toString());
		data.append("has_horizontal_scrollbar", (document.body.scrollWidth > window.innerWidth).toString());

		fetch("/whisper/neon/", {
			method: "POST",
			body: data,
			headers: {
				"x-client-session-id": window.CLIENT_SESSION_ID,
				"x-csrftoken": window.CSRF_TOKEN,
				"x-requested-with": "XMLHttpRequest",
			},
		}).catch(noop);
	},
	900000,
	{ leading: true },
); // 15 minutes in milliseconds

document.addEventListener("click", debouncedInteractionTracker);
